import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { AlertCircle, CheckCircle, Download, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const IBGPage = ({ user }) => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    isbn_input: '',
    book_title: '',
    book_type: 'Paperback',
    price: '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    opt_in: false
  });
  
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [barcodeData, setBarcodeData] = useState(null);
  const [emailSending, setEmailSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.isbn_input.trim()) {
      toast.error('Please enter an ISBN');
      return;
    }
    
    // For non-logged-in users, require email and opt-in
    if (!user && !formData.email) {
      toast.error('Please provide your email address');
      return;
    }
    
    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null
      };
      
      const response = await fetch(`${API}/api/ibg/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setBarcodeData(data);
        setShowModal(true);
        toast.success('Barcode generated successfully!');
        
        // Auto-close modal after 30 seconds
        setTimeout(() => {
          handleCloseModal();
        }, 30000);
      } else {
        toast.error(data.error_message || 'Failed to generate barcode');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred while generating the barcode');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!barcodeData) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${barcodeData.barcode_image_base64}`;
    link.download = `isbn-${barcodeData.normalized_isbn13}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Barcode downloaded!');
  };

  const handleEmailBarcode = async () => {
    if (!barcodeData) return;
    
    const emailAddress = formData.email || user?.email;
    if (!emailAddress) {
      toast.error('Please provide an email address');
      return;
    }
    
    setEmailSending(true);
    
    try {
      const formBody = new URLSearchParams();
      formBody.append('email', emailAddress);
      formBody.append('barcode_base64', barcodeData.barcode_image_base64);
      formBody.append('isbn', barcodeData.normalized_isbn13);
      
      const response = await fetch(`${API}/api/ibg/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody
      });
      
      if (response.ok) {
        toast.success('Barcode emailed successfully! Check your inbox.');
      } else {
        toast.error('Failed to send email');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred while sending the email');
    } finally {
      setEmailSending(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setBarcodeData(null);
    // Reset entire form for next generation
    setFormData({
      isbn_input: '',
      book_title: '',
      book_type: 'Paperback',
      price: '',
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      opt_in: false
    });
    
    // Redirect to originating page
    if (user) {
      navigate('/homepage'); // Logged in users go to dashboard
    } else {
      navigate('/'); // Non-logged in users go to landing page
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header with Logo and Navigation */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/Digital_Ink_AI_Logo.png" 
                alt="Digital Ink AI Logo" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                }}
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/homepage')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                🏠 Home
              </button>
              <button
                onClick={() => navigate('/user_dashboard')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                📊 Dashboard
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Service Title and Description */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            📊 ISBN Barcode Generator
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Generate professional, print-ready ISBN barcodes in seconds. Transform your ISBN-10 or ISBN-13 into industry-standard EAN-13 Bookland barcodes optimized for back-cover placement. Receive high-resolution PNG files (300 DPI) with instant download and optional email delivery. Completely free with no usage restrictions, registration, or hidden fees.
          </p>
        </div>

        {/* Pricing Info */}
        <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
          <div className="text-center">
            <h3 className="text-xl font-bold text-green-900 mb-2">✨ 100% Free Forever</h3>
            <p className="text-green-700">No registration required • No usage limits • No hidden fees</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Barcode Generation Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg border border-slate-200">
              {/* Form Header with Logo */}
              <div className="p-6 border-b border-slate-200">
                <div className="text-center mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                    alt="Digital Ink AI" 
                    className="h-10 mx-auto mb-3"
                  />
                  <h2 className="text-2xl font-bold text-slate-900">📊 Generate Your Barcode</h2>
                  <p className="text-sm text-slate-600 mt-1">Powered by Digital Ink AI</p>
                </div>
              </div>

              {/* Form Content */}
              <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
              {/* ISBN Input */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  ISBN Number *
                </label>
                <input
                  type="text"
                  value={formData.isbn_input}
                  onChange={(e) => setFormData(prev => ({ ...prev, isbn_input: e.target.value }))}
                  placeholder="978-1-234-56789-7 or 1-234-56789-X"
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter ISBN-10 or ISBN-13 (with or without dashes)
                </p>
              </div>

              {/* Book Title */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Book Title (Optional)
                </label>
                <input
                  type="text"
                  value={formData.book_title}
                  onChange={(e) => setFormData(prev => ({ ...prev, book_title: e.target.value }))}
                  placeholder="Enter your book title"
                  className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Title will appear above the barcode
                </p>
              </div>

              {/* Book Type Field */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Book Type
                </label>
                <select
                  value={formData.book_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, book_type: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="Paperback">Paperback</option>
                  <option value="Hardcover">Hardcover</option>
                  <option value="Ebook">Ebook</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Book type will appear with the title
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  <span className="text-red-600">*</span> Price (Optional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="19.99"
                  className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Price will appear with book type on same line as title
                </p>
              </div>

              {/* Non-logged-in user fields */}
              {!user && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                        className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                        className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <input
                      type="checkbox"
                      checked={formData.opt_in}
                      onChange={(e) => setFormData(prev => ({ ...prev, opt_in: e.target.checked }))}
                      className="mt-1 w-4 h-4"
                      required
                    />
                    <label className="text-sm text-slate-700">
                      I agree to receive helpful publishing tips and updates from Digital Ink AI. 
                      You can unsubscribe anytime.
                    </label>
                  </div>
                </>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
              >
                {loading ? '⏳ Generating...' : '📊 Generate Barcode with Digital Ink AI'}
              </button>
            </form>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Generation Features */}
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">🎯 Generation Features</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start">
                  <span className="text-blue-500 mr-2">✓</span>
                  <span>Industry-standard EAN-13 Bookland barcode format</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-500 mr-2">✓</span>
                  <span>High-resolution PNG (300 DPI, print-ready)</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-500 mr-2">✓</span>
                  <span>Instant download to your device</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-500 mr-2">✓</span>
                  <span>Optional email delivery to your inbox</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-500 mr-2">✓</span>
                  <span>Placement guidance for back-cover printing</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-500 mr-2">✓</span>
                  <span>Accepts ISBN-10 or ISBN-13 formats</span>
                </div>
              </div>
            </div>

            {/* Usage Tips */}
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">💡 Usage Tips</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start">
                  <span className="text-amber-500 mr-2">•</span>
                  <span><strong>ISBN Format:</strong> Enter with or without dashes</span>
                </div>
                <div className="flex items-start">
                  <span className="text-amber-500 mr-2">•</span>
                  <span><strong>Placement:</strong> Lower right of back cover</span>
                </div>
                <div className="flex items-start">
                  <span className="text-amber-500 mr-2">•</span>
                  <span><strong>Size:</strong> Minimum 1.5" wide for scanners</span>
                </div>
                <div className="flex items-start">
                  <span className="text-amber-500 mr-2">•</span>
                  <span><strong>Quality:</strong> 300 DPI for professional printing</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Barcode Modal */}
      {showModal && barcodeData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <CheckCircle className="w-8 h-8" />
                Barcode Generated Successfully!
              </h2>
            </div>

            <div className="p-6">
              {/* Barcode Image */}
              <div className="bg-white border-4 border-slate-200 rounded-lg p-6 mb-6 text-center">
                <img
                  src={`data:image/png;base64,${barcodeData.barcode_image_base64}`}
                  alt="ISBN Barcode"
                  className="max-w-full h-auto mx-auto"
                />
              </div>

              {/* Summary */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
                <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">
                  {barcodeData.summary_text}
                </pre>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleDownload}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PNG
                </Button>
                <Button
                  onClick={handleEmailBarcode}
                  disabled={emailSending}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {emailSending ? 'Sending...' : 'Email Me'}
                </Button>
                <Button
                  onClick={handleCloseModal}
                  variant="outline"
                  className="flex-1 border-slate-300 hover:bg-slate-50 font-semibold py-3"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IBGPage;
